import { z } from 'zod';

import { TOOL_NAMES } from '../toolNames';

export interface AskQuestionOption {
    label: string;
}

/**
 * The kind of answer a question collects, which selects the widget used to
 * answer it. Option-based kinds are answered from a choice list the LLM authors;
 * app-driven kinds are answered through one of the app's own pickers.
 */
export const QUESTION_TYPES = [
    'radio',
    'checkbox',
    'clip_select',
    'free_text',
    'aspect_ratio',
    'file_upload',
    'voice',
    'template',
    'brand',
    'waveform',
] as const;

export type QuestionType = (typeof QUESTION_TYPES)[number];

/**
 * The kinds answered from an LLM-authored choice list (or, for `free_text` and
 * `aspect_ratio`, from a fixed control rendered in its place). Everything else is
 * app-driven and must register a widget — see `CustomQuestionType`.
 */
export const OPTION_BASED_QUESTION_TYPES = [
    'radio',
    'checkbox',
    'clip_select',
    'free_text',
    'aspect_ratio',
] as const satisfies readonly QuestionType[];

export type OptionBasedQuestionType = (typeof OPTION_BASED_QUESTION_TYPES)[number];

/**
 * The kinds whose answer comes back from a picker rather than from a choice list.
 * Derived by subtraction, so adding a type to `QUESTION_TYPES` without listing
 * it above makes it custom — and the widget registry then fails to compile
 * until it has a widget, rather than silently falling through to radios.
 */
export type CustomQuestionType = Exclude<QuestionType, OptionBasedQuestionType>;

/** What kind of file a `file_upload` question accepts. */
export const FILE_UPLOAD_ACCEPTS = ['audio', 'image', 'video', 'image_or_video'] as const;

export type FileUploadAccept = (typeof FILE_UPLOAD_ACCEPTS)[number];

/**
 * Kinds answered by a picker that needs nothing from the agent: no choices to
 * author, no parameters to set. Grouped into one branch so the union still covers
 * every `QuestionType` exactly once.
 */
const PARAMETERLESS_QUESTION_TYPES = [
    'free_text',
    'aspect_ratio',
    'brand',
    'waveform',
] as const satisfies readonly QuestionType[];

const CHOICE_SCHEMA = z.object({
    label: z.string().describe('Concise text for the choice. Plain text only — no markdown.'),
});

/**
 * The one field every question carries, whatever its type. Deliberately
 * undescribed: it appears in every branch of the `anyOf`, so its guidance lives
 * once on the `questions` array instead of being repeated per branch.
 */
const questionText = z.string();

/**
 * One question, forked by `type` so each kind declares exactly the parameters it
 * uses — and no others:
 *
 * - `accept` is required on `file_upload`, where the widget needs it to know what
 *   files to take.
 * - Each choice list is named for what it holds: `choices` for labels the user
 *   reads, `voice_slugs` and `template_ids` for registry ids.
 * - The kinds answered by a picker that needs nothing from the agent declare no
 *   choice field at all.
 *
 * `z.union` rather than `z.discriminatedUnion`: both narrow identically in
 * TypeScript, but `discriminatedUnion` emits JSON-Schema `oneOf`, which none of
 * our three providers support (OpenAI, Anthropic, and Vertex all accept `anyOf`
 * and reject `oneOf`). `zodJsonSchema` throws on an emitted `oneOf` to keep that
 * mistake from reaching a provider.
 *
 * A parameter sent on the wrong type (say `accept` on a `voice` question) is
 * stripped by validation, not rejected: the resulting question is identical
 * either way, so failing the call would cost the agent a turn for no gain. The
 * fork's value is on the reading side — a widget can only see the parameters its
 * own type declares.
 */
export const askQuestionSchema = z.union([
    z.object({
        question: questionText,
        type: z
            .enum(['radio', 'checkbox'] as const satisfies readonly QuestionType[])
            .describe('"radio" = pick one, "checkbox" = pick any number.'),
        choices: z
            .array(CHOICE_SCHEMA)
            .min(2)
            .describe(
                'The 2-4 choices offered. An "Other" free-text field is added automatically, so do not add one.',
            ),
    }),
    z.object({
        question: questionText,
        type: z
            .literal('clip_select')
            .describe(
                'Pick any number of playable video clips cut from one recording. Picking none is a valid answer.',
            ),
        clip_source_media_id: z
            .string()
            .describe(
                'Media id of the source recording the clips are cut from — an uploaded video or audio file.',
            ),
        clips: z
            .array(
                z.object({
                    label: z
                        .string()
                        .describe('Concise text for the clip. Plain text only — no markdown.'),
                    words: z
                        .array(z.string())
                        .min(1)
                        .describe(
                            'The clip\'s transcript words, in order, copied EXACTLY as the transcript spells them — including filler words like "um" and any words the speaker repeats. Do not tidy the sentence up: these words are matched against the transcript to find the clip, and a cleaned-up quote matches nothing. The clip is the span the words occupy, so it starts and ends on a word.',
                        ),
                }),
            )
            .min(1)
            .describe('The clips offered, each previewed as a playable card.'),
    }),
    z.object({
        question: questionText,
        type: z.literal('file_upload').describe('Upload one or more files.'),
        accept: z.enum(FILE_UPLOAD_ACCEPTS).describe('What kind of file to accept.'),
    }),
    z.object({
        question: questionText,
        type: z.literal('voice').describe('Voice picker.'),
        voice_slugs: z
            .array(z.string())
            .optional()
            .describe(`Voices to suggest, by slug from ${TOOL_NAMES.searchVoices}.`),
        language: z
            .string()
            .optional()
            .describe(
                `The language ${TOOL_NAMES.searchVoices} resolved for these suggestions, e.g. "en", so each one previews a sample in it rather than an arbitrary default.`,
            ),
    }),
    z.object({
        question: questionText,
        type: z.literal('template').describe('Template picker.'),
        template_ids: z.array(z.string()).optional().describe('Templates to suggest, by id.'),
    }),
    z.object({
        question: questionText,
        type: z
            .enum(PARAMETERLESS_QUESTION_TYPES)
            .describe(
                '"free_text" = type an answer, "aspect_ratio" = pick an aspect ratio, "brand" = pick one of the workspace\'s saved brand kits, "waveform" = waveform-style picker.',
            ),
    }),
]);

/** The `type` values the union above actually accepts. */
type UnionQuestionType = z.infer<typeof askQuestionSchema>['type'];

/**
 * Every `QuestionType` must appear in exactly one branch of the union above, and
 * the union must add none of its own. A type added to `QUESTION_TYPES` but
 * forgotten in the union would otherwise be rejected at runtime — the model would
 * get a validation error for a type the enum told it to use.
 *
 * Both directions are asserted as `never`, so drift fails the build here (naming
 * the stray type) rather than only at whichever call site happens to use it.
 */
export type QuestionTypesMissingFromUnion = Exclude<QuestionType, UnionQuestionType>;
export type UnionTypesMissingFromQuestionTypes = Exclude<UnionQuestionType, QuestionType>;
const _noQuestionTypeIsMissing: never = null as unknown as QuestionTypesMissingFromUnion;
const _noUnionTypeIsUnknown: never = null as unknown as UnionTypesMissingFromQuestionTypes;
void _noQuestionTypeIsMissing;
void _noUnionTypeIsUnknown;

/**
 * One question as authored by the LLM and consumed by the UI alike: each kind
 * carries exactly the parameters its own branch declares. Read the choices
 * through {@link choicesOf} rather than reaching for a per-type field.
 */
export type AskQuestion = z.infer<typeof askQuestionSchema>;

/**
 * The choices a question offers, whatever its type calls them. Every surface
 * consumes them the same way — a list of labels to render or resolve — so they
 * are read through here rather than per-field at each call site. The kinds that
 * take no choices return an empty list.
 */
export function choicesOf(question: AskQuestion): AskQuestionOption[] {
    if ('choices' in question) {
        return question.choices;
    }
    if ('clips' in question) {
        return question.clips;
    }
    if ('voice_slugs' in question) {
        return (question.voice_slugs ?? []).map((label) => ({ label }));
    }
    if ('template_ids' in question) {
        return (question.template_ids ?? []).map((label) => ({ label }));
    }
    return [];
}

/**
 * Narrows an {@link AskQuestion} to a single `type`, for a widget's props.
 * Distributes over `T` as well as over the union, so it also resolves for a type
 * that shares a branch with others (the parameterless kinds are one branch, but
 * `AskQuestionOfType<'brand'>` still names just the brand question).
 */
export type AskQuestionOfType<T extends QuestionType> = T extends unknown
    ? Extract<AskQuestion, { type: T }> extends never
        ? Extract<AskQuestion, { type: QuestionType }> & { type: T }
        : Extract<AskQuestion, { type: T }>
    : never;

/** A resolved answer from an app-driven widget. */
export interface ResolvedAnswer {
    kind: CustomQuestionType;
    /** What the user picked, e.g. "Aria (Warm, Female)". Display text only. */
    name: string;
    /**
     * Registry id of the picked item, when the widget has one. Lets the calling
     * tool act on the exact thing chosen instead of matching `name` back against
     * a catalog it fetched separately.
     */
    id?: string;
    /**
     * Agent-facing references for the picked assets, one per line, in the same
     * `### MEDIA/AUDIO: … | ID: … ###` form uploads use elsewhere. A
     * bare filename is not actionable — the agent needs these IDs to put the
     * asset into the video, so they're surfaced instead of `name` when present.
     */
    references?: string[];
    /**
     * Set while an upload is still in flight. Submitting then would drop the
     * asset (it has no reference yet), so the surfaces block their
     * Continue/Submit button until this clears.
     */
    pending?: boolean;
}

/** One picked clip, echoed back so callers get the clip itself, not just a label. */
export interface SelectedClip {
    /** Position in the question's clips, so callers can index straight back. */
    index: number;
    label: string;
    /**
     * The clip's transcript words — what a consumer passes on to act on it.
     * Times are deliberately not echoed: the words locate the clip against
     * whichever transcript the consumer is working with, so nothing can drift
     * from a stale timestamp.
     */
    words: string[];
}

export interface AskQuestionAnswer {
    question: string;
    selectedLabels: string[];
    /** Free-text the user typed into the "Other" field, if any. */
    customText?: string;
    /**
     * For `clip_select` questions: the picked clips echoed back (empty when the
     * user deliberately picked none, absent on every other type).
     */
    selectedClips?: SelectedClip[];
    resolved?: ResolvedAnswer;
}

export type AskUserQuestionResult =
    | { status: 'answered'; answers: AskQuestionAnswer[] }
    | { status: 'dismissed' };

export interface PendingAskUserQuestion {
    questions: AskQuestion[];
    resolve: (result: AskUserQuestionResult) => void;
}
